import {ForgotForm, PasswordForm, SignInForm, SignUpForm, SocialAuthParams} from "ecom-types";
import {OAuthProvider, signInWithPopup} from "firebase/auth";


export function useAuth() {

    interface OnSignInUpOptions {
        successHandler?: () => void,
        errorHandler?: (error?: any) => void,
    }

    enum ProviderIdsE {
        "google.com" = "google.com",
        "facebook.com" = "facebook.com",
        "apple.com" = "apple.com"
    }

    interface Provider { id: keyof typeof ProviderIdsE, name: string, icon: string };

    const { $api, $socialAuth } = useNuxtApp();
    const { cartUniqueId, clientIp, wishlistId } = useCookiesService();
    const localePath = useLocalePathPolyfill();
    const { locale } = useI18n();
    const router = useRouter();
    const route = useRoute();
    const generalStore = useGeneralStore();

    async function onSignIn(values: SignInForm) {
        const params = {
            ...values,
        };
        if (cartUniqueId.value) {
            params.cart_unique_id = cartUniqueId.value;
        }
        if (clientIp.value) {
            params.client_ip = clientIp.value;
        }

        try {
            const data = await $api.auth.signIn(params);
            generalStore.setUserInfo(data.data || null, true);

            return data
            // if(options.successHandler) {
            //     await options.successHandler();
            // }
        } catch (error) {
            throw error
            // if(options.errorHandler) {
            //     options.errorHandler(error);
            // }
        }
    }

    async function onSignUp(values: SignUpForm) {
        try {
            return await $api.auth.signUp(values)
        } catch (error) {
            throw error
        }

    }

    async function onResetPassword(values: ForgotForm) {
        try {
            return await $api.user.recoveryPasswordRequest(values);
        } catch (error) {
            throw error
        }
    }

    async function onForgotPassword(values: PasswordForm, options: { token: string }) {
        try {
            return await $api.user.changePasswordByRecoveryToken({
                password_recovery_token: options.token,
                ...values,
            })
        } catch (error) {
            throw error
        }
    }

    async function onVerify(token: string) {
        const params = {
            email_verification_token: token,
        };

        return useAsyncData("verify-email", () => {
            return $api.user.verifyEmail(params);
        });
    }

    async function logout(options?: OnSignInUpOptions) {
        try {
            await $socialAuth.signOut();
        } catch (error) {
            throw error
        } finally {
            wishlistId.value = null;
            await nextTick();
            generalStore.wishlist = undefined;
            generalStore.setUserInfo(null);
            generalStore.setCart(null);
        }
    }

    function getProviderScopes(providerId: keyof typeof ProviderIdsE) {
        switch (providerId) {
            case "google.com":
                return ["profile", "email"];
            case "facebook.com":
                return ["public_profile", "email"];

            case "apple.com":
                return ["name", "email"];

            default:
                return [];
        }
    }

    async function authByProviderId(providerId: keyof typeof ProviderIdsE) {
        if (!$socialAuth?.auth) return;

        const provider = new OAuthProvider(providerId);
        const scopes = getProviderScopes(providerId);
        scopes.forEach((scope) => {
            provider.addScope(scope);
        });

        provider.setCustomParameters({
            locale: locale.value,
        });

        try {
            const result = await signInWithPopup($socialAuth.auth, provider);

            if (result.user) {
                const params: SocialAuthParams = {
                    token: result.user.accessToken,
                };
                if (cartUniqueId.value) {
                    params.cart_unique_id = cartUniqueId.value;
                }

                const response = await $api.auth.socialAuth(params);

                generalStore.setUserInfo(response.data || null);

                return { firebaseResult: result, userData: response.data }
            } else {
                return new Error('User not found');
            }
        } catch (error) {
            throw error
        }
    }

    return {
        onSignIn,
        logout,
        onSignUp,
        onResetPassword,
        onForgotPassword,
        onVerify,
        authByProviderId,
    };
}
